import { Button } from '@/components/Button';
import { Collapse } from '@/components/Collapse/Collapse';
import { DatePicker } from '@/components/DatePicker';
import { DaysPickerDropdown } from '@/components/DaysPickerDropdown';
import { DomainPreStep } from '@/components/DomainPreStep';
import {
  ChevronDownIcon,
  CopyIcon,
  ElevationIcon,
  LiftIcon,
  MountBlackIcon,
  MountBlueIcon,
  MountGreenIcon,
  MountRedIcon,
  RouteIcon,
  WarningIcon,
} from '@/components/icons';
import { SkiAreaDropdown } from '@/components/SkiAreaDropdown';
import { SkierTypesDropdown } from '@/components/SkierTypesDropdown';
import { NO_OFFERS_OUTSIDE_SEASON_MESSAGE } from '@/constants/inventory-domain.constant';
import { useTripDetailsSeason } from '@/hooks/useTripDetailsSeason';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { type CSSProperties, useState } from 'react';
import styles from './TripDetailsPage.module.css';
import { useTripDetailsForm } from './useTripDetailsForm';

export const TripDetailsPage = () => {
  const [isCopied, setIsCopied] = useState(false);
  const selectedDomain = useBookSkiPassStore((state) => state.selectedDomain);

  const [isDateValid, setIsDateValid] = useState(true);

  const {
    stepTwoFormFieldsValues,
    bookingReference,
    resortName,
    isSubmitDisabled: isSubmitDisabledBase,
    handleDomainChange,
    needsDomainPreStep,
    handlePreStepDomainConfirm,
    minDate,
    handleSkiersLevelSelect,
    handleSkiStyleSelect,
    handleSkierCountsChange,
    handleDaysChange,
    handleDateChange,
    handleSubmit,
    ctaInlineMessage,
  } = useTripDetailsForm();

  const isTripSubmitLoading = useBookSkiPassStore((state) => state.isLoading);

  const season = useTripDetailsSeason();

  const isSubmitDisabled = isSubmitDisabledBase || !isDateValid || isTripSubmitLoading;

  if (needsDomainPreStep) {
    return <DomainPreStep resortName={resortName} onConfirm={handlePreStepDomainConfirm} />;
  }

  const formatSlopeDistance = (lengthInKm: number | undefined): string => {
    if (lengthInKm == null) return '0';
    return String(Math.round(lengthInKm));
  };

  const formatElevation = (meters: number | undefined): string => {
    if (meters == null) return '0';
    return Math.round(meters).toLocaleString();
  };

  const hasTotalMarkedRuns = selectedDomain?.all_length != null;
  const hasElevationRange = selectedDomain?.low != null && selectedDomain?.high != null;
  const hasVerticalDrop = selectedDomain?.drop != null;
  const hasAtAGlanceStats = hasTotalMarkedRuns || hasElevationRange || hasVerticalDrop;

  const hasGreenSlope = selectedDomain?.green_amount != null;
  const hasBlueSlope = selectedDomain?.blue_amount != null;
  const hasRedSlope = selectedDomain?.red_amount != null;
  const hasBlackSlope = selectedDomain?.black_amount != null;
  const hasSlopesBreakdown = hasGreenSlope || hasBlueSlope || hasRedSlope || hasBlackSlope;
  const hasLiftAmount = selectedDomain?.lift_amount != null;
  const hasLiftAndSlopesStats = hasLiftAmount || hasSlopesBreakdown;

  const handleCopyReference = async () => {
    try {
      await navigator.clipboard.writeText(bookingReference);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className={styles.contentContainerInner}>
      <div>
        <div className={styles.infoCardContainer}>
          {bookingReference && (
            <div className={styles.bookingRef}>
              <span>Reference: {bookingReference}</span>
              <div className={styles.copyButtonWrapper}>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={() => {
                    void handleCopyReference();
                  }}
                  title="Copy booking reference"
                >
                  <CopyIcon className={styles.copyIcon} />
                </button>
                {isCopied && <div className={styles.tooltip}>Copied</div>}
              </div>
            </div>
          )}
          <div
            className={styles.infoCard}
            style={
              selectedDomain?.image?.trim()
                ? ({
                    ['--trip-info-card-photo' as string]: `url(${JSON.stringify(selectedDomain.image.trim())})`,
                  } as CSSProperties)
                : undefined
            }
          >
            <div className={styles.infoCardContent}>
              <div className={styles.infoCardContentLeft}>
                <div className={styles.headerColumn}>
                  {resortName && (
                    <div className={styles.goingTo}>
                      <span className={styles.goingToLabel}>Going to:</span>
                      {` ${resortName}`}
                    </div>
                  )}
                  <div className={styles.resortName}>
                    <h2>{selectedDomain?.title ?? ''}</h2>
                    <div className={styles.skiAreaDropdownWrapper}>
                      <SkiAreaDropdown value={selectedDomain} onChange={handleDomainChange} />
                    </div>
                  </div>
                  {selectedDomain?.resorts && selectedDomain.resorts.length > 0 && (
                    <div className={styles.resortList}>
                      <p>{selectedDomain.resorts.map((r) => r.title).join(' • ')}</p>
                    </div>
                  )}
                  <div className={styles.mobileMoreDetails}>
                    <Collapse
                      summary="More details"
                      summaryOpen="Close details"
                      chevron={<ChevronDownIcon className={styles.moreDetailsChevron} />}
                    >
                      <div className={styles.mobileExpandContent}>
                        <div className={styles.mobileStats}>
                          {hasTotalMarkedRuns && (
                            <div className={styles.statItem}>
                              <div className={styles.statLabelWithIcon}>
                                <div className={styles.statLabel}>Total marked runs</div>
                                <RouteIcon className={styles.statIcon} />
                              </div>
                              <div className={styles.statValue}>
                                {`~${formatSlopeDistance(selectedDomain?.all_length)} km`}
                              </div>
                            </div>
                          )}

                          {(hasElevationRange || hasVerticalDrop) && (
                            <div className={`${styles.statItem ?? ''} ${styles.withDivider ?? ''}`}>
                              {hasElevationRange && (
                                <div>
                                  <div className={styles.statLabelWithIcon}>
                                    <div className={styles.statLabel}>Elevation range</div>
                                    <ElevationIcon className={styles.statIcon} />
                                  </div>
                                  <div className={styles.statValue}>
                                    {`${formatElevation(selectedDomain?.low)} - ${formatElevation(selectedDomain?.high)} m`}
                                  </div>
                                </div>
                              )}
                              {hasElevationRange && hasVerticalDrop && <div className={styles.statDivider} />}
                              {hasVerticalDrop && (
                                <div className={styles.statItemDouble}>
                                  <div className={styles.statValue}>{`${formatElevation(selectedDomain?.drop)} m`}</div>
                                  <div className={styles.statLabel}>Vertical drop</div>
                                </div>
                              )}
                            </div>
                          )}

                          {hasLiftAndSlopesStats && (
                            <div
                              className={`${styles.statItem ?? ''} ${hasLiftAmount && hasSlopesBreakdown ? (styles.withDivider ?? '') : ''}`}
                            >
                              {hasLiftAmount && (
                                <div>
                                  <div className={styles.statLabelWithIcon}>
                                    <div className={styles.statLabel}>Ski lifts</div>
                                    <LiftIcon className={styles.statIcon} />
                                  </div>
                                  <div className={styles.statValue}>{`${String(selectedDomain?.lift_amount)} lifts`}</div>
                                </div>
                              )}

                              {hasLiftAmount && hasSlopesBreakdown && (
                                <div className={styles.statDivider} />
                              )}

                              {hasSlopesBreakdown && (
                                <div className={styles.slopesBreakdown}>
                                  {hasGreenSlope && (
                                    <div className={styles.slopeItem}>
                                      <div className={styles.slopeItemLeft}>
                                        <MountGreenIcon className={styles.slopeIcon} />
                                        <span className={styles.slopeName}>Easy</span>
                                      </div>
                                      <span className={styles.slopeDetails}>
                                        {`${String(selectedDomain?.green_amount)} slopes${selectedDomain?.green_length != null && selectedDomain.green_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.green_length)} km` : ''}`}
                                      </span>
                                    </div>
                                  )}
                                  {hasBlueSlope && (
                                    <div className={styles.slopeItem}>
                                      <div className={styles.slopeItemLeft}>
                                        <MountBlueIcon className={styles.slopeIcon} />
                                        <span className={styles.slopeName}>Intermediate</span>
                                      </div>
                                      <span className={styles.slopeDetails}>
                                        {`${String(selectedDomain?.blue_amount)} slopes${selectedDomain?.blue_length != null && selectedDomain.blue_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.blue_length)} km` : ''}`}
                                      </span>
                                    </div>
                                  )}
                                  {hasRedSlope && (
                                    <div className={styles.slopeItem}>
                                      <div className={styles.slopeItemLeft}>
                                        <MountRedIcon className={styles.slopeIcon} />
                                        <span className={styles.slopeName}>Advanced</span>
                                      </div>
                                      <span className={styles.slopeDetails}>
                                        {`${String(selectedDomain?.red_amount)} slopes${selectedDomain?.red_length != null && selectedDomain.red_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.red_length)} km` : ''}`}
                                      </span>
                                    </div>
                                  )}
                                  {hasBlackSlope && (
                                    <div className={styles.slopeItem}>
                                      <div className={styles.slopeItemLeft}>
                                        <MountBlackIcon className={styles.slopeIcon} />
                                        <span className={styles.slopeName}>Expert</span>
                                      </div>
                                      <span className={styles.slopeDetails}>
                                        {`${String(selectedDomain?.black_amount)} slopes${selectedDomain?.black_length != null && selectedDomain.black_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.black_length)} km` : ''}`}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Collapse>
                  </div>
                </div>
              </div>
              <div className={styles.infoCardContentRight}>
                {hasAtAGlanceStats && (
                  <div className={styles.statsColumn}>
                    {hasTotalMarkedRuns && (
                      <div className={styles.statItem}>
                        <div className={styles.statLabelWithIcon}>
                          <div className={styles.statLabel}>Total marked runs</div>
                          <RouteIcon className={styles.statIcon} />
                        </div>
                        <div className={styles.statValue}>
                          {`~${formatSlopeDistance(selectedDomain?.all_length)} km`}
                        </div>
                      </div>
                    )}

                    {(hasElevationRange || hasVerticalDrop) && (
                      <div className={`${styles.statItem ?? ''} ${styles.withDivider ?? ''}`}>
                        {hasElevationRange && (
                          <div>
                            <div className={styles.statLabelWithIcon}>
                              <div className={styles.statLabel}>Elevation range</div>
                              <ElevationIcon className={styles.statIcon} />
                            </div>
                            <div className={styles.statValue}>
                              {`${formatElevation(selectedDomain?.low)} - ${formatElevation(selectedDomain?.high)} m`}
                            </div>
                          </div>
                        )}
                        {hasElevationRange && hasVerticalDrop && <div className={styles.statDivider} />}
                        {hasVerticalDrop && (
                          <div className={styles.statItemDouble}>
                            <div className={styles.statValue}>{`${formatElevation(selectedDomain?.drop)} m`}</div>
                            <div className={styles.statLabel}>Vertical drop</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {hasLiftAndSlopesStats && (
                  <div className={styles.statsColumn}>
                    <div
                      className={`${styles.statItem ?? ''} ${hasLiftAmount && hasSlopesBreakdown ? (styles.withDivider ?? '') : ''}`}
                    >
                      {hasLiftAmount && (
                        <div>
                          <div className={styles.statLabelWithIcon}>
                            <div className={styles.statLabel}>Ski lifts</div>
                            <LiftIcon className={styles.statIcon} />
                          </div>
                          <div className={styles.statValue}>{`${String(selectedDomain?.lift_amount)} lifts`}</div>
                        </div>
                      )}

                      {hasLiftAmount && hasSlopesBreakdown && <div className={styles.statDivider} />}

                      {hasSlopesBreakdown && (
                        <div className={styles.slopesBreakdown}>
                          {hasGreenSlope && (
                            <div className={styles.slopeItem}>
                              <div className={styles.slopeItemLeft}>
                                <MountGreenIcon className={styles.slopeIcon} />
                                <span className={styles.slopeName}>Easy</span>
                              </div>
                              <span className={styles.slopeDetails}>
                                {`${String(selectedDomain?.green_amount)} slopes${selectedDomain?.green_length != null && selectedDomain.green_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.green_length)} km` : ''}`}
                              </span>
                            </div>
                          )}
                          {hasBlueSlope && (
                            <div className={styles.slopeItem}>
                              <div className={styles.slopeItemLeft}>
                                <MountBlueIcon className={styles.slopeIcon} />
                                <span className={styles.slopeName}>Intermediate</span>
                              </div>
                              <span className={styles.slopeDetails}>
                                {`${String(selectedDomain?.blue_amount)} slopes${selectedDomain?.blue_length != null && selectedDomain.blue_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.blue_length)} km` : ''}`}
                              </span>
                            </div>
                          )}
                          {hasRedSlope && (
                            <div className={styles.slopeItem}>
                              <div className={styles.slopeItemLeft}>
                                <MountRedIcon className={styles.slopeIcon} />
                                <span className={styles.slopeName}>Advanced</span>
                              </div>
                              <span className={styles.slopeDetails}>
                                {`${String(selectedDomain?.red_amount)} slopes${selectedDomain?.red_length != null && selectedDomain.red_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.red_length)} km` : ''}`}
                              </span>
                            </div>
                          )}
                          {hasBlackSlope && (
                            <div className={styles.slopeItem}>
                              <div className={styles.slopeItemLeft}>
                                <MountBlackIcon className={styles.slopeIcon} />
                                <span className={styles.slopeName}>Expert</span>
                              </div>
                              <span className={styles.slopeDetails}>
                                {`${String(selectedDomain?.black_amount)} slopes${selectedDomain?.black_length != null && selectedDomain.black_length > 0 ? ` • ${formatSlopeDistance(selectedDomain.black_length)} km` : ''}`}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <h3 className={styles.formTitle}>Plan your ski trip</h3>
        <p className={styles.formSubtitle}>
          Tell us about your group and dates to find the best ski pass options
        </p>

        <form
          className={styles.tripForm}
          onSubmit={(e) => {
            handleSubmit(e);
          }}
        >
          <div className={styles.requiredSection}>
            <div className={styles.formCard}>
              <h4 className={styles.cardTitle}>Main trip details</h4>
              <p className={styles.cardDescription}>
                We've pre-filled some details based on your booking.
                <br />
                You can change them if needed.
              </p>

              {season.showNoOffersForDateRange && (
                <div className={styles.noOffersSeasonBanner} role="alert">
                  <WarningIcon className={styles.noOffersSeasonBannerIcon} />
                  <span>{NO_OFFERS_OUTSIDE_SEASON_MESSAGE}</span>
                </div>
              )}

              <div className={styles.mobileFormFields}>
                <div className={styles.formField}>
                  <label className={styles.label}>
                    Who's skiing? <span className={styles.required}>*</span>
                  </label>
                  <SkierTypesDropdown
                    value={stepTwoFormFieldsValues.skierCounts}
                    onChange={handleSkierCountsChange}
                    restrictions={selectedDomain?.restrictions}
                    hasSelectedDomain={Boolean(selectedDomain)}
                  />
                  <p className={styles.fieldHelp}>
                    Used to apply the correct pass types and prices for everyone in your group
                  </p>
                </div>

                <div className={styles.formField}>
                  <label className={styles.label}>
                    Days of skiing <span className={styles.required}>*</span>
                  </label>
                  <DaysPickerDropdown
                    value={stepTwoFormFieldsValues.daysOfSkiing}
                    onChange={handleDaysChange}
                  />
                  <p className={styles.fieldHelp}>
                    Ski days are counted consecutively from the start date
                  </p>
                </div>

                <div className={styles.formField}>
                  <label className={styles.label}>
                    Starting date <span className={styles.required}>*</span>
                  </label>
                  <DatePicker
                    value={stepTwoFormFieldsValues.startingDate}
                    onChange={handleDateChange}
                    onValidationChange={setIsDateValid}
                    minDate={minDate}
                    dropdownPosition="above"
                    isDateAllowed={season.applies ? season.isStartDateAllowed : undefined}
                    getSeasonCalendarFooterText={
                      season.applies ? season.getSeasonCalendarFooterText : undefined
                    }
                    disallowedSelectionMessage={
                      season.applies ? season.disallowedStartDateMessage : undefined
                    }
                  />

                  {season.showSkipassExtendsBeyondSeasonNotice && (
                    <div className={styles.seasonExtensionNotice}>
                      <WarningIcon className={styles.seasonExtensionNoticeIcon} />
                      <span>{season.skipassBeyondSeasonCopy}</span>
                    </div>
                  )}

                  {isDateValid && (
                    <p className={styles.fieldHelp}>
                      This will be the first day your ski passes are valid
                    </p>
                  )}
                </div>
              </div>

              {ctaInlineMessage === 'noOffers' && (
                <div className={styles.ctaInlineWarning} role="status">
                  <WarningIcon className={styles.ctaInlineWarningIcon} aria-hidden />
                  <span>
                    <strong>No offers found.</strong> Adjust your start date or duration.
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.submitSection}>
            <Button type="submit" variant="solid" size="large" disabled={isSubmitDisabled} className={styles.submitButton}>
              Check ski pass offers
            </Button>
            {ctaInlineMessage === 'technical' && (
              <div className={styles.ctaInlineError} role="alert">
                <WarningIcon className={styles.ctaInlineErrorIcon} aria-hidden />
                <span>
                  <strong>Something went wrong.</strong> Please try again later.
                </span>
              </div>
            )}
            <p className={styles.submitHelp}>
              Next, we&apos;ll show ski pass options for your group — filtered by dates, group size and
              preferences
            </p>
          </div>

          <div className={`${styles.optionalSection ?? ''} ${styles.disabled ?? ''}`}>
            <div className={styles.formCard}>
              <h4 className={styles.cardTitle}>Personalize your recommendations</h4>
              <p className={styles.cardDescription}>
                Want more tailored ski pass suggestions for your group?
                <br />
                Answer a few quick questions to fine-tune your results.
              </p>

              <div className={styles.formField}>
                <label className={styles.label}>What's your group average ski level?</label>
                <div className={styles.buttonGroup}>
                  <button
                    type="button"
                    className={`${styles.optionButton ?? ''} ${stepTwoFormFieldsValues.skiersLevel === 'beginner' ? (styles.selected ?? '') : ''}`}
                    onClick={() => {
                      handleSkiersLevelSelect('beginner');
                    }}
                    disabled
                  >
                    Beginner
                  </button>
                  <button
                    type="button"
                    className={`${styles.optionButton ?? ''} ${stepTwoFormFieldsValues.skiersLevel === 'intermediate' ? (styles.selected ?? '') : ''}`}
                    onClick={() => {
                      handleSkiersLevelSelect('intermediate');
                    }}
                    disabled
                  >
                    Intermediate
                  </button>
                  <button
                    type="button"
                    className={`${styles.optionButton ?? ''} ${stepTwoFormFieldsValues.skiersLevel === 'advanced' ? (styles.selected ?? '') : ''}`}
                    onClick={() => {
                      handleSkiersLevelSelect('advanced');
                    }}
                    disabled
                  >
                    Advanced
                  </button>
                </div>
                <p className={styles.fieldHelp}>
                  Select the overall ski level of your group (not individual skiers). We'll use
                  this to recommend ski areas and passes suitable for everyone
                </p>
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>
                  How does your group usually ski (ski style)?
                </label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name="skiStyle"
                      checked={stepTwoFormFieldsValues.skiStyle === 'fullDays'}
                      onChange={() => {
                        handleSkiStyleSelect('fullDays');
                      }}
                      disabled
                    />
                    <div className={styles.radioContent}>
                      <div className={styles.radioTitle}>Full days on the slopes</div>
                      <div className={styles.radioDescription}>
                        We ski most of the day, every day
                      </div>
                    </div>
                  </label>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name="skiStyle"
                      checked={stepTwoFormFieldsValues.skiStyle === 'balancedPace'}
                      onChange={() => {
                        handleSkiStyleSelect('balancedPace');
                      }}
                      disabled
                    />
                    <div className={styles.radioContent}>
                      <div className={styles.radioTitle}>Balanced pace</div>
                      <div className={styles.radioDescription}>We ski 4–6 hours per day</div>
                    </div>
                  </label>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name="skiStyle"
                      checked={stepTwoFormFieldsValues.skiStyle === 'relaxed'}
                      onChange={() => {
                        handleSkiStyleSelect('relaxed');
                      }}
                      disabled
                    />
                    <div className={styles.radioContent}>
                      <div className={styles.radioTitle}>Relaxed / Après-ski style</div>
                      <div className={styles.radioDescription}>
                        We ski a few hours per day with breaks
                      </div>
                    </div>
                  </label>
                </div>
                <p className={styles.fieldHelp}>
                  We&apos;ll match suggestions to how many hours your group spends on the slopes each
                  day.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
